import React, { useEffect, useRef } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';
import { BaseComponentProps, WithChildrenProps } from '../types/componentThemes';
import { useReducedMotion } from '../hooks/useReducedMotion';

export type NotificationVariant = 'success' | 'warning' | 'error' | 'info';

export interface NotificationProps
  extends BaseComponentProps,
    Omit<WithChildrenProps, 'children'> {
  /** Notification type variant */
  variant: NotificationVariant;
  /** Notification message */
  message: string;
  /** Optional title */
  title?: string;
  /** Whether to show close button */
  showClose?: boolean;
  /** Callback when notification is dismissed */
  onClose?: () => void;
  /** Auto-dismiss duration in milliseconds (0 to disable) */
  autoDismiss?: number;
}

export const Notification: React.FC<NotificationProps> = ({
  variant,
  message,
  title,
  showClose = true,
  onClose,
  autoDismiss = 5000,
  className = '',
  style,
  testId,
}) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (autoDismiss > 0 && onClose) {
      timerRef.current = setTimeout(() => {
        onClose();
      }, autoDismiss);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [autoDismiss, onClose]);

  const baseClasses = [
    'flex',
    'items-start',
    'gap-3',
    'p-4',
    'rounded-xl',
    'shadow-lg',
    'min-w-[320px]',
    'max-w-md',
    'backdrop-blur-sm',
    !prefersReducedMotion && 'animate-slideInRight',
  ].filter(Boolean);

  const variantClasses: Record<NotificationVariant, string[]> = {
    success: [
      'bg-white/95',
      'dark:bg-neutral-800/95',
      'border',
      'border-success-DEFAULT/30',
      'text-neutral-900',
      'dark:text-neutral-100',
    ],
    warning: [
      'bg-white/95',
      'dark:bg-neutral-800/95',
      'border',
      'border-warning-DEFAULT/30',
      'text-neutral-900',
      'dark:text-neutral-100',
    ],
    error: [
      'bg-white/95',
      'dark:bg-neutral-800/95',
      'border',
      'border-error-DEFAULT/30',
      'text-neutral-900',
      'dark:text-neutral-100',
    ],
    info: [
      'bg-white/95',
      'dark:bg-neutral-800/95',
      'border',
      'border-info-DEFAULT/30',
      'text-neutral-900',
      'dark:text-neutral-100',
    ],
  };

  const icons: Record<NotificationVariant, React.ReactNode> = {
    success: <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-success-DEFAULT" />,
    warning: <AlertTriangle className="w-5 h-5 flex-shrink-0 text-warning-DEFAULT" />,
    error: <XCircle className="w-5 h-5 flex-shrink-0 text-error-DEFAULT" />,
    info: <Info className="w-5 h-5 flex-shrink-0 text-info-DEFAULT" />,
  };

  const notificationClasses = [
    ...baseClasses,
    ...variantClasses[variant],
    className,
  ].join(' ');

  const ariaRole = variant === 'error' ? 'alert' : 'status';

  return (
    <div
      className={notificationClasses}
      style={style}
      data-testid={testId}
      role={ariaRole}
      aria-live={variant === 'error' ? 'assertive' : 'polite'}
    >
      {/* Icon */}
      <div className="mt-0.5" data-testid={testId ? `${testId}-icon` : undefined}>
        {icons[variant]}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <h4
            className="font-semibold text-sm mb-0.5"
            data-testid={testId ? `${testId}-title` : undefined}
          >
            {title}
          </h4>
        )}
        <p
          className="text-sm text-neutral-600 dark:text-neutral-300"
          data-testid={testId ? `${testId}-message` : undefined}
        >
          {message}
        </p>
      </div>

      {/* Close Button */}
      {showClose && onClose && (
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 p-1 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current"
          aria-label="Close notification"
          data-testid={testId ? `${testId}-close-button` : 'notification-close-button'}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// Re-export NotificationContainer for convenience
export { NotificationContainer } from './NotificationContainer';
export type { NotificationContainerProps, NotificationItem } from './NotificationContainer';
